import { expressQuerySelector, expressEventListener, expressQuerySelectorAll } from "../common/html";
import { createCollapsable, ICollapsable } from "./collapsable";
import { getParameter, updateParameter, queryremovekeys } from "../utils/querystring";
import { initShowMoreElements } from "./show-more-elements";
import { trackEvent } from "../common/events";

interface ISliderDeps {
	readonly removeSliderFilter: () => void;
}

enum TemplateType {
	Text,
	Icon,
}

enum TypeOfFilter {
	Regular,
	MultiSelect,
	Icons,
	Slider
}

export function createFilters(containerEl: HTMLElement, valueClicked: (filterName: string, filterValue: string, checked: boolean, containerEl: HTMLElement) => void = null, resetAll: (containerEl: HTMLElement) => void = null): { resetFiltersContainerEl: () => void } {
	const selectedFiltersContainerEl = expressQuerySelector<HTMLElement>(containerEl, '.technical-selectedfilters', false);
	hideSelectedFilters(containerEl, selectedFiltersContainerEl);

	const collapsibleEls = expressQuerySelectorAll<HTMLElement>(containerEl, '.m-collapsable');
	const filterQs = [];

	collapsibleEls.forEach(el => {
		if (el.classList.contains('technical-selectedfilters')) return; // skip the selected filters collapsible

		const filtercategory = el.dataset.filtercategory;
		const filterqs = el.dataset.filterqs;

		const filterCollapsiable = createCollapsable(el, { titleElSelector: '.m-collapsable__title', scrollIntoViewAfterOpen: false, openClass: 'is-collapse-open' });
		const showmore = expressQuerySelector<HTMLElement>(el, '.technical-showmore', false);
		filterQs.push(filterqs);

		if (showmore) {
			filterQs.push(filterqs ? `showmore${filterqs.toLowerCase()}` : null);
			initShowMoreElements(el, showmore, filterqs ? `showmore${filterqs.toLowerCase()}` : null, '.technical-filter-value');
		}

		const filterCheckboxEls = expressQuerySelectorAll<HTMLElement>(el, '.a-form-input--checkbox, .a-form-input--radio');
		filterCheckboxEls.forEach(filterEl => {
			if (TypeOfFilter[el.dataset.typeoffilter] === TypeOfFilter.Regular || TypeOfFilter[el.dataset.typeoffilter] === TypeOfFilter.MultiSelect)
				createCheckBoxSelectFilter(filterCollapsiable, TypeOfFilter[el.dataset.typeoffilter], selectedFiltersContainerEl, filterqs, filtercategory, filterEl, filterCheckboxEls, containerEl, valueClicked);
		});

		const colorEls = expressQuerySelectorAll<HTMLElement>(el, '.a-icon--color');
		colorEls.forEach(filterEl => {
			createColorFilter(filterCollapsiable, selectedFiltersContainerEl, filterqs, filtercategory, filterEl, containerEl);
		});

		const sliderEls = expressQuerySelectorAll<HTMLElement>(el, '.a-form-input--slider');
		sliderEls.forEach(filterEl => {
			const showSliderEls = expressQuerySelectorAll<HTMLInputElement>(el, 'input[type=checkbox],input[type=radio]');
			createSliderFilter(filterCollapsiable, selectedFiltersContainerEl, filterqs, filtercategory, filterEl, showSliderEls, containerEl);
		});
	});

	const resetFilters = expressQuerySelectorAll<HTMLElement>(containerEl, '.technical-reset-filters');
	const resetFiltersContainerEl = () => {
		trackEvent('Filters', 'FilterReset', 'Reset', -1);
		if (resetAll)
			resetAll(containerEl);

		collapsibleEls.forEach(collapsibleEl => { if (collapsibleEl.classList.contains('is-collapse-open')) collapsibleEl.classList.remove('is-collapse-open'); });

		const colorEls = expressQuerySelectorAll<HTMLElement>(containerEl, '.a-icon--color');
		colorEls.forEach(colorEl => {
			if (colorEl.classList.contains('a-icon--selected')) {
				colorEl.classList.remove('a-icon--selected');
				colorEl.classList.add('a-icon--unselected');
			}
		});

		const filterEls = expressQuerySelectorAll<HTMLInputElement>(containerEl, '.technical-filter');
		filterEls.forEach(filterEl => {
			const checkboxElsPerFilter = expressQuerySelectorAll<HTMLInputElement>(filterEl, 'input[type=checkbox]:not(.technical-sorting-radio),input[type=radio]:not(.technical-sorting-radio)');
			let batchsizeDesktop = -1;
			let batchsizeMobile = -1;

			checkboxElsPerFilter.forEach((checkboxEl, indx) => {
				const showMoreEl = expressQuerySelector<HTMLElement>(checkboxEl.closest(".m-collapsable__content"), ".technical-no-hide-parrent", false);

				if (showMoreEl) {
					batchsizeDesktop = showMoreEl.dataset.batchsizeDesktop ? +showMoreEl.dataset.batchsizeDesktop : batchsizeDesktop;
					batchsizeMobile = showMoreEl.dataset.batchsizeMobile ? +showMoreEl.dataset.batchsizeMobile : batchsizeMobile;

					if (indx > batchsizeDesktop - 1) {
						if (showMoreEl.classList.contains("u-hide-on-desktop-full")) showMoreEl.classList.remove("u-hide-on-desktop-full");
						checkboxEl.closest(".technical-filter-value").classList.add("u-hide-on-desktop-full");
					}

					if (indx > batchsizeMobile - 1) {
						if (showMoreEl.classList.contains("u-hide-on-mobile-full")) showMoreEl.classList.remove("u-hide-on-mobile-full");
						checkboxEl.closest(".technical-filter-value").classList.add("u-hide-on-mobile-full");
					}
				}

				checkboxEl.checked = false;
			});
		});

		const showSliderEls = expressQuerySelectorAll<HTMLElement>(containerEl, '.a-form-input--slider');
		showSliderEls.forEach(showSliderEl => {
			const checkBoxEls = expressQuerySelectorAll<HTMLInputElement>(showSliderEl.parentElement, 'input[type=checkbox],input[type=radio]');
			const sliderFilter = createSliderFilter(null, selectedFiltersContainerEl, showSliderEl.parentElement.parentElement.dataset.filterqs, showSliderEl.parentElement.parentElement.dataset.filtercategory, showSliderEl, checkBoxEls, containerEl);

			sliderFilter.removeSliderFilter();
		});

		const filterTagEls = expressQuerySelectorAll<HTMLElement>(selectedFiltersContainerEl, '.a-filter-tag');
		filterTagEls.forEach(el => el.remove());

		hideSelectedFilters(containerEl, selectedFiltersContainerEl);
		window.history.pushState({}, null, queryremovekeys(filterQs));
	};
	resetFilters.forEach(resetFilter => expressEventListener(resetFilter, "click", resetFiltersContainerEl));
	const filterEl = expressQuerySelector(containerEl, '.technical-filters-container', false) || containerEl;
	filterEl.classList.remove('skeleton-loading');

	return {
		resetFiltersContainerEl
	};
}

function createSliderFilter(collapsiableComponent: ICollapsable, selectedFiltersContainerEl: HTMLElement, filterQs: string, filterCategory: string, filterEl: HTMLElement, showSliderEls: HTMLInputElement[], filtersContainerEl: HTMLElement): ISliderDeps {
	const outOfBoundsMinBarEl = expressQuerySelector<HTMLElement>(filterEl, ".a-form-input__slider-pre-bar", true);
	const outOfBoundsMaxBarEl = expressQuerySelector<HTMLElement>(filterEl, ".a-form-input__slider-post-bar", true);
	const sliderMinRangeInputEl = expressQuerySelector<HTMLInputElement>(filterEl, ".a-form-input__slider-range--min", true);
	const sliderMaxRangeInputEl = expressQuerySelector<HTMLInputElement>(filterEl, ".a-form-input__slider-range--max", true);
	const sliderMinRangeThumbEl = expressQuerySelector<HTMLElement>(filterEl, ".a-form-input__slider-thumb--min", true);
	const sliderMaxRangeThumbEl = expressQuerySelector<HTMLElement>(filterEl, ".a-form-input__slider-thumb--max", true);

	const containerEl = expressQuerySelector<HTMLElement>(filterEl, ".a-form-input__slider-container", true);
	const containerMinSliderTextInputEl = expressQuerySelector<HTMLElement>(containerEl, ".a-form-input__slider-bubble-container--min", true);
	const containerMaxSliderTextInputEl = expressQuerySelector<HTMLElement>(containerEl, ".a-form-input__slider-bubble-container--max", true);
	const sliderMinTextInputEl = expressQuerySelector<HTMLInputElement>(containerMinSliderTextInputEl, ".a-form-input__slider-bubble--min", true);
	const sliderMaxTextInputEl = expressQuerySelector<HTMLInputElement>(containerMaxSliderTextInputEl, ".a-form-input__slider-bubble--max", true);

	const originalMinValue = sliderMinRangeInputEl.min;
	const originalMaxValue = sliderMaxRangeInputEl.max;

	const selectedFilter = getParameter(filterQs);

	const deselect = (currentEl?: HTMLInputElement | null) => {
		if (showSliderEls.length) filterEl.classList.add("u-hide");

		showSliderEls.forEach(chk => {
			if (currentEl && currentEl.id !== chk.id) chk.checked = false;
			if (!currentEl) chk.checked = false;
		});
	};

	const drawSlider = (sliderRangeInputEl: HTMLInputElement, sliderRangeThumbEl: HTMLElement, sliderTextInputEl: HTMLInputElement, containerSliderTextInputEl: HTMLElement, outOfBoundsBarEl: HTMLElement, isLeftSliderMoved: boolean, isTextInputChanged: boolean) => {
		const difference = (containerSliderTextInputEl.offsetWidth / 10) + 1;

		if (isTextInputChanged) {
			sliderTextInputEl.value = isLeftSliderMoved ?
				Math.min(parseInt(sliderTextInputEl.value) < parseInt(sliderMinRangeInputEl.min) ? parseInt(sliderMinRangeInputEl.min) : parseInt(sliderTextInputEl.value) || parseInt(sliderMaxRangeInputEl.min), parseInt(sliderMaxRangeInputEl.value) - difference).toString() :
				Math.max(parseInt(sliderTextInputEl.value) > parseInt(sliderMaxRangeInputEl.max) ? parseInt(sliderMaxRangeInputEl.max) : parseInt(sliderTextInputEl.value) || parseInt(sliderMinRangeInputEl.max), parseInt(sliderMinRangeInputEl.value) + difference).toString();
			sliderRangeInputEl.value = sliderTextInputEl.value;
		} else {
			sliderRangeInputEl.value = isLeftSliderMoved ?
				Math.min(parseInt(sliderRangeInputEl.value), parseInt(sliderMaxRangeInputEl.value) - difference).toString() :
				Math.max(parseInt(sliderRangeInputEl.value), parseInt(sliderMinRangeInputEl.value) + difference).toString();
			sliderTextInputEl.value = sliderRangeInputEl.value;
		}

		const percent = Math.ceil((parseInt(sliderRangeInputEl.value) / parseInt(sliderRangeInputEl.max)) * 100);
		const percentStyle = percent + '%';
		const reversePercentStyle = 100 - percent + '%';

		const inBoundsBarEl = expressQuerySelector<HTMLElement>(filterEl, ".a-form-input__slider-range", true);
		if (isLeftSliderMoved) {
			inBoundsBarEl.style.left = percentStyle;
			outOfBoundsBarEl.style.width = percentStyle;
		}
		else {
			inBoundsBarEl.style.right = reversePercentStyle;
			outOfBoundsBarEl.style.width = reversePercentStyle;
		}

		sliderRangeThumbEl.style.left = percentStyle;
		containerSliderTextInputEl.style.left = percentStyle;
	};

	const hideSlider = (el: HTMLInputElement) => {
		if (!showSliderEls.length) return;

		if (showSliderEls.every(f => !f.checked))
			removeSliderFilter();
		else {
			deselect(el);
			if (el.classList.contains("technical-show-slider") && el.checked && filterEl.classList.contains("u-hide")) {
				addTextToFilterContainer(filtersContainerEl, TypeOfFilter.Slider, selectedFiltersContainerEl, filterCategory, sliderMinTextInputEl.value + " - " + sliderMaxTextInputEl.value, removeSliderFilter);
				filterEl.classList.remove("u-hide");
			} else
				addTextToFilterContainer(filtersContainerEl, TypeOfFilter.Slider
					, selectedFiltersContainerEl, filterCategory, "0", removeSliderFilter);
		}
	};

	const addSliderFilter = (sliderRangeInputEl: HTMLInputElement, sliderRangeThumbEl: HTMLElement, sliderTextInputEl: HTMLInputElement, containerSliderTextInputEl: HTMLElement, outOfBoundsBarEl: HTMLElement, isLeftSliderMoved: boolean, isTextInputChanged: boolean) => {
		drawSlider(sliderRangeInputEl, sliderRangeThumbEl, sliderTextInputEl, containerSliderTextInputEl, outOfBoundsBarEl, isLeftSliderMoved, isTextInputChanged);

		addTextToFilterContainer(filtersContainerEl, TypeOfFilter.Slider, selectedFiltersContainerEl, filterCategory, sliderMinTextInputEl.value + " - " + sliderMaxTextInputEl.value, removeSliderFilter);
	};

	const removeSliderFilter = () => {
		deselect();

		sliderMinTextInputEl.value = originalMinValue;
		sliderMaxTextInputEl.value = originalMaxValue;
		sliderMinRangeInputEl.value = originalMinValue;
		sliderMaxRangeInputEl.value = originalMaxValue;

		drawSlider(sliderMinRangeInputEl, sliderMinRangeThumbEl, sliderMinTextInputEl, containerMinSliderTextInputEl, outOfBoundsMinBarEl, true, false);
		drawSlider(sliderMaxRangeInputEl, sliderMaxRangeThumbEl, sliderMaxTextInputEl, containerMaxSliderTextInputEl, outOfBoundsMaxBarEl, false, false);

		updateParametersForUrl(TypeOfFilter.Slider, filterQs, sliderMinRangeInputEl.value + "-" + sliderMaxTextInputEl.value, true, null, filterEl.dataset.analytics);
		removeFromFilterContainer(filtersContainerEl, selectedFiltersContainerEl, filterCategory);
	};

	if (selectedFiltersContainerEl)
		attachRemoveFilterEvent(selectedFiltersContainerEl, filterCategory, removeSliderFilter);

	expressEventListener(sliderMinRangeInputEl, 'input', function () { updateParametersForUrl(TypeOfFilter.Slider, filterQs, sliderMinRangeInputEl.value + "-" + sliderMaxTextInputEl.value, false, addSliderFilter(sliderMinRangeInputEl, sliderMinRangeThumbEl, sliderMinTextInputEl, containerMinSliderTextInputEl, outOfBoundsMinBarEl, true, false), filterEl.dataset.analytics); });
	expressEventListener(sliderMaxRangeInputEl, 'input', function () { updateParametersForUrl(TypeOfFilter.Slider, filterQs, sliderMinTextInputEl.value + "-" + sliderMaxRangeInputEl.value, false, addSliderFilter(sliderMaxRangeInputEl, sliderMaxRangeThumbEl, sliderMaxTextInputEl, containerMaxSliderTextInputEl, outOfBoundsMaxBarEl, false, false), filterEl.dataset.analytics); });
	expressEventListener(sliderMinTextInputEl, 'change', function () { updateParametersForUrl(TypeOfFilter.Slider, filterQs, sliderMinTextInputEl.value + "-" + sliderMaxTextInputEl.value, false, addSliderFilter(sliderMinRangeInputEl, sliderMinRangeThumbEl, sliderMinTextInputEl, containerMinSliderTextInputEl, outOfBoundsMinBarEl, true, true), filterEl.dataset.analytics); });
	expressEventListener(sliderMaxTextInputEl, 'change', function () { updateParametersForUrl(TypeOfFilter.Slider, filterQs, sliderMinTextInputEl.value + "-" + sliderMaxTextInputEl.value, false, addSliderFilter(sliderMaxRangeInputEl, sliderMaxRangeThumbEl, sliderMaxTextInputEl, containerMaxSliderTextInputEl, outOfBoundsMaxBarEl, false, true), filterEl.dataset.analytics); });

	showSliderEls.forEach(el => expressEventListener(el, 'click', function () { updateParametersForUrl(TypeOfFilter.Slider, filterQs, !el.classList.contains('technical-show-slider') ? "0" : sliderMinTextInputEl.value + "-" + sliderMaxTextInputEl.value, (el.classList.contains('technical-show-slider') && !el.checked), hideSlider(el), filterEl.dataset.analytics); }));

	if (selectedFilter && collapsiableComponent) {
		toggleResetFilters(filtersContainerEl, false);
		collapsiableComponent.open();
	}

	return {
		removeSliderFilter
	};
}

function createColorFilter(collapsiableComponent: ICollapsable, selectedFiltersContainerEl: HTMLElement, filterqs: string, filterCategory: string, filterEl: HTMLElement, containerEl: HTMLElement) {
	const selectedFilterName = filterEl.dataset.filterelement;
	const selectedFilter = decodeURI(getParameter(filterqs)).split(',');

	const removeColorFilter = () => {
		filterEl.classList.remove("a-icon--selected");
		filterEl.classList.add("a-icon--unselected");

		updateParametersForUrl(TypeOfFilter.Icons, filterqs, selectedFilterName, true, null, filterEl.dataset.analytics);
		removeFromFilterContainer(containerEl, selectedFiltersContainerEl, filterCategory, selectedFilterName);
	};

	const toggleColorFilter = () => {
		if (filterEl.classList.contains("a-icon--selected")) {
			filterEl.classList.remove("a-icon--selected");
			filterEl.classList.add("a-icon--unselected");

			removeFromFilterContainer(containerEl, selectedFiltersContainerEl, filterCategory, selectedFilterName);
		} else {
			filterEl.classList.remove("a-icon--unselected");
			filterEl.classList.add("a-icon--selected");

			addColorToFilterContainer(containerEl, selectedFiltersContainerEl, selectedFilterName, filterCategory, removeColorFilter);
		}
	};

	if (selectedFiltersContainerEl)
		attachRemoveFilterEvent(selectedFiltersContainerEl, filterCategory, removeColorFilter, filterEl.dataset.filterelement);

	expressEventListener(filterEl, 'click', function () { updateParametersForUrl(TypeOfFilter.Icons, filterqs, filterEl.dataset.filterelement, filterEl.classList.contains("a-icon--selected"), toggleColorFilter(), filterEl.dataset.analytics); });

	if (selectedFilter && selectedFilter.includes(filterEl.dataset.filterelement.toLowerCase())) {
		if (!filterEl.classList.contains('a-icon--selected')) toggleColorFilter();
		collapsiableComponent.open();
		toggleResetFilters(containerEl, false);
	}
}

function createCheckBoxSelectFilter(collapsiableComponent: ICollapsable, typeOfFilter: TypeOfFilter, selectedFiltersContainerEl: HTMLElement, filterqs: string, filterCategory: string, filterEl: HTMLElement, checkboxEls: HTMLElement[], containerEl: HTMLElement, valueClicked: (filterName: string, filterValue: string, checked: boolean, containerEl: HTMLElement) => void) {
	const selectedFilterName = filterEl.dataset.filterelement;
	const selectedFilter = decodeURI(getParameter(filterqs)).split(',');

	const removeSingleSelectFilter = () => {
		if (typeOfFilter === TypeOfFilter.Regular)
			checkboxEls.forEach(el => {
				const checkboxEl = expressQuerySelector<HTMLInputElement>(el, 'input[type=checkbox],input[type=radio]', true);
				checkboxEl.checked = false;
			});
		else {
			const filterCheckboxEl = expressQuerySelector<HTMLInputElement>(filterEl, 'input[type=checkbox],input[type=radio]', true);
			filterCheckboxEl.checked = false;
		}

		if (valueClicked)
			valueClicked(filterCategory, filterCheckboxEl.value, filterCheckboxEl.checked, containerEl);
		updateParametersForUrl(typeOfFilter, filterqs, selectedFilterName, true, null, filterEl.dataset.analytics);
		removeFromFilterContainer(containerEl, selectedFiltersContainerEl, filterCategory, selectedFilterName);
	};

	const addSingleSelectFilter = (checkBoxEl: HTMLInputElement) => {
		if (typeOfFilter === TypeOfFilter.Regular)
			checkboxEls.filter(f => f.dataset.filterelement != selectedFilterName).forEach(el => {
				const checkboxEl = expressQuerySelector<HTMLInputElement>(el, 'input[type=checkbox],input[type=radio]', false);
				if (checkboxEl)
					checkboxEl.checked = false;
			});

		if (checkBoxEl.checked)
			addTextToFilterContainer(containerEl, typeOfFilter, selectedFiltersContainerEl, filterCategory, selectedFilterName, removeSingleSelectFilter);
		else
			removeFromFilterContainer(containerEl, selectedFiltersContainerEl, filterCategory, selectedFilterName);
	};

	if (selectedFiltersContainerEl)
		attachRemoveFilterEvent(selectedFiltersContainerEl, filterCategory, removeSingleSelectFilter, selectedFilterName);

	const filterCheckboxEl = expressQuerySelector<HTMLInputElement>(filterEl, 'input[type=checkbox],input[type=radio]', true);
	expressEventListener(filterEl, 'change', function () {
		if (valueClicked)
			valueClicked(filterCategory, filterCheckboxEl.value, filterCheckboxEl.checked, containerEl);
		if (filterqs)
			updateParametersForUrl(typeOfFilter, filterqs, filterEl.dataset.filterelement, !filterCheckboxEl.checked, addSingleSelectFilter(filterCheckboxEl), filterEl.dataset.analytics);
		else {
			trackEvent('Filters', 'FilterClicked', `${filterCategory}_${filterEl.dataset.analytics || filterEl.dataset.filterelement}`, -1);
			addSingleSelectFilter(filterCheckboxEl);
		}
	});

	if (selectedFilter && filterCheckboxEl && selectedFilterName && selectedFilter.includes(selectedFilterName.toLowerCase())) {
		filterCheckboxEl.checked = true;

		if (valueClicked)
			valueClicked(filterCategory, filterCheckboxEl.value, filterCheckboxEl.checked, containerEl);
		toggleResetFilters(containerEl, false);
		addSingleSelectFilter(filterCheckboxEl);
		collapsiableComponent.open();
	}
}

function addFilterTemplate(templateType: TemplateType, filterCategory: string, selectedFilterName: string, htmlOrColor: string, removeFilterEvent: any): HTMLDivElement {
	const div = document.createElement('div');
	div.dataset.filterforcategory = filterCategory;
	div.classList.add("a-filter-tag", templateType === TemplateType.Text ? "a-filter-tag--text" : "a-filter-tag--color");
	div.dataset.filterforcategoryvalue = selectedFilterName;

	if (templateType === TemplateType.Text) {
		const p = document.createElement('p');
		p.classList.add("a-filter-tag__label");
		const content = document.createTextNode(htmlOrColor);
		p.appendChild(content);
		div.appendChild(p);
	}
	else {
		const iconSpan = document.createElement('span');
		iconSpan.classList.add("a-icon", "a-icon--color", "a-icon--unselected", "a-icon--small", "a-icon--no-hover", "a-icon-" + htmlOrColor);
		iconSpan.style.color = htmlOrColor;
		if (htmlOrColor === "white" || htmlOrColor === "a-icon--nocolor") iconSpan.classList.add("a-icon--outline");
		const icon = document.createElement('i');
		icon.classList.add("a-icon__icon");
		const iconColorSpan = document.createElement('span');
		iconColorSpan.classList.add("a-icon__color");
		icon.appendChild(iconColorSpan);
		iconSpan.appendChild(icon);
		div.appendChild(iconSpan);
	}

	const span = document.createElement('span');
	span.dataset.eventalreadybinded = "true";
	span.classList.add("a-filter-tag__remove", "icon-close");
	span.addEventListener("click", removeFilterEvent);
	div.appendChild(span);

	return div;
}

function removeFromFilterContainer(filtersContainerEl: HTMLElement, selectedFiltersContainerEl: HTMLElement, filterCategory: string, filterCategoryValue?: string) {
	const selector = !filterCategoryValue ? `[data-filterforcategory="${filterCategory}"]` : `[data-filterforcategory="${filterCategory}"][data-filterforcategoryvalue="${filterCategoryValue}"]`;
	const selectedFilterCategoryEl = expressQuerySelector<HTMLElement>(selectedFiltersContainerEl, selector, false);
	if (selectedFilterCategoryEl) selectedFilterCategoryEl.remove();

	hideSelectedFilters(filtersContainerEl, selectedFiltersContainerEl);
}

function addTextToFilterContainer(filtersContainerEl: HTMLElement, typeOfFilter: TypeOfFilter, selectedFiltersContainerEl: HTMLElement, filterCategory: string, selectedFilterName: string, removeFilterEvent: any) {
	showSelectedFilters(filtersContainerEl, selectedFiltersContainerEl);

	if (!selectedFiltersContainerEl)
		return;

	const selector = typeOfFilter === TypeOfFilter.Regular || typeOfFilter === TypeOfFilter.Slider ? `[data-filterforcategory="${filterCategory}"]` : `[data-filterforcategory="${filterCategory}"][data-filterforcategoryvalue="${selectedFilterName}"]`;
	const selectedFilterCategoryEl = expressQuerySelector<HTMLElement>(selectedFiltersContainerEl, selector, false);
	if (selectedFilterCategoryEl) {
		const filterCategoryContentEl = expressQuerySelector<HTMLElement>(selectedFilterCategoryEl, 'p.a-filter-tag__label', true);
		filterCategoryContentEl.innerHTML = typeOfFilter === TypeOfFilter.Slider ? filterCategory + ": " + selectedFilterName : selectedFilterName;
		return;
	}

	const filter = addFilterTemplate(TemplateType.Text, filterCategory, selectedFilterName, typeOfFilter === TypeOfFilter.Slider ? filterCategory + ": " + selectedFilterName : selectedFilterName, removeFilterEvent);
	const allSelectedColorFilters = expressQuerySelectorAll<HTMLElement>(selectedFiltersContainerEl, `[data-filterforcategory="${filterCategory}"]`);
	if (allSelectedColorFilters.length) {
		allSelectedColorFilters[allSelectedColorFilters.length - 1].insertAdjacentElement('afterend', filter);
		return;
	}

	const filters = expressQuerySelectorAll(document, '[data-typeoffilter][data-filtercategory]') as HTMLElement[];
	const compair = (val1: HTMLElement) => filters.findIndex(e => val1.dataset.filterforcategory === e.dataset.filtercategory);
	const selectedFiltersChilderen = expressQuerySelectorAll(selectedFiltersContainerEl, '[data-filterforcategory]');
	const index = findInsertIndex(selectedFiltersChilderen, filter, compair);
	const elementToInsertBefore = selectedFiltersChilderen[index];
	if (!elementToInsertBefore || index <= -1 || index > selectedFiltersChilderen.length) {
		selectedFiltersContainerEl.appendChild(filter);
		return;
	}
	elementToInsertBefore.insertAdjacentElement('beforebegin', filter);
}

function attachRemoveFilterEvent(selectedFiltersContainerEl: HTMLElement, filterForCategory: string, removeEvent: any, filterForCategoryValue?: string) {
	const selectedFilterCategoryEl = expressQuerySelector<HTMLElement>(selectedFiltersContainerEl, !filterForCategoryValue ? `[data-filterforcategory="${filterForCategory}"]` : `[data-filterforcategory="${filterForCategory}"][data-filterforcategoryvalue="${filterForCategoryValue}"]`, false);

	if (selectedFilterCategoryEl) {
		const removeEl = expressQuerySelector<HTMLElement>(selectedFilterCategoryEl, '.a-filter-tag__remove', false);
		if (removeEl && (!removeEl.dataset.eventalreadybinded || removeEl.dataset.eventalreadybinded !== "true")) {
			removeEl.dataset.eventalreadybinded = "true";
			removeEl.addEventListener("click", removeEvent);
		}
	}
}

function addColorToFilterContainer(containerEl: HTMLElement, selectedFiltersContainerEl: HTMLElement, selectedFilterName: string, filterForCategory: string, removeFilterEvent: any) {
	showSelectedFilters(containerEl, selectedFiltersContainerEl);

	if (!selectedFiltersContainerEl)
		return;

	const selectedFilterEl = expressQuerySelector<HTMLElement>(selectedFiltersContainerEl, `[data-filterforcategory="${filterForCategory}"][data-filterforcategoryvalue="${selectedFilterName}"]`, false);
	if (selectedFilterEl) return;

	const colorfilter = addFilterTemplate(TemplateType.Icon, filterForCategory, selectedFilterName, selectedFilterName, removeFilterEvent);
	const allSelectedColorFilters = expressQuerySelectorAll<HTMLElement>(selectedFiltersContainerEl, `[data-filterforcategory="${filterForCategory}"]`);
	if (allSelectedColorFilters.length) {
		allSelectedColorFilters[allSelectedColorFilters.length - 1].insertAdjacentElement('afterend', colorfilter);
		return;
	}
	const compair = (val1: HTMLElement) => filters.findIndex(e => val1.dataset.filterforcategory === e.dataset.filtercategory);
	const filters = expressQuerySelectorAll(document, '[data-typeoffilter][data-filtercategory]') as HTMLElement[];
	const selectedFiltersChilderen = expressQuerySelectorAll(selectedFiltersContainerEl, '[data-filterforcategory]');
	const index = findInsertIndex(selectedFiltersChilderen, colorfilter, compair);
	const elementToInsertBefore = selectedFiltersChilderen[index];
	if (!elementToInsertBefore || index <= -1 || index > selectedFiltersChilderen.length) {
		selectedFiltersContainerEl.appendChild(colorfilter);
		return;
	}
	elementToInsertBefore.insertAdjacentElement('beforebegin', colorfilter);
}

function findInsertIndex(arr, val, getIndexOfElement): number {
	// Lower and upper bounds
	let start = 0;
	let end = arr.length - 1;
	const indexOfElement = getIndexOfElement(val);

	// Traverse the search space
	while (start <= end) {
		const mid = Math.floor((start + end) / 2);
		const index = getIndexOfElement(arr[mid]);

		// If K is found
		if (index == indexOfElement)
			return mid;
		else if (index < indexOfElement)
			start = mid + 1;
		else
			end = mid - 1;
	}

	// Return insert position
	return end + 1;
}

function hideSelectedFilters(filtersContainerEl: HTMLElement, selectedFiltersContainerEl: HTMLElement) {
	const resetFilterEls = expressQuerySelectorAll<HTMLElement>(filtersContainerEl, '.o-filters__button-reset');

	if (!selectedFiltersContainerEl) {
		resetFilterEls.forEach(resetFilterEl => resetFilterEl.classList.add("u-hide"));
		return;
	}

	const filterContainerTagEls = expressQuerySelectorAll(selectedFiltersContainerEl.parentElement, '.a-filter-tag');

	if (!filterContainerTagEls.length && !selectedFiltersContainerEl.classList.contains("u-hide")) {
		selectedFiltersContainerEl.classList.add("u-hide");
		resetFilterEls.forEach(resetFilterEl => resetFilterEl.classList.add("u-hide"));
	}
}

function toggleResetFilters(filtersContainerEl: HTMLElement, force?: boolean) {
	const resetFilterEls = expressQuerySelectorAll<HTMLElement>(filtersContainerEl, '.o-filters__button-reset');
	resetFilterEls.forEach(resetFilterEl => resetFilterEl.classList.toggle("u-hide", force));
}

function showSelectedFilters(filtersContainerEl: HTMLElement, selectedFiltersContainerEl: HTMLElement) {
	toggleResetFilters(filtersContainerEl, false);

	if (!selectedFiltersContainerEl)
		return;

	if (selectedFiltersContainerEl.classList.contains("u-hide")) {
		selectedFiltersContainerEl.classList.remove("u-hide");
	}
}

function updateParametersForUrl(typeOfFilter: TypeOfFilter, filterQs: string, msg: string, removeFromUrl: boolean, eventToHandle: void | null, analytics?: string) {
	eventToHandle;
	trackEvent('Filters', 'FilterClicked', analytics ? analytics : `${filterQs}_${msg}`, -1);

	if (!filterQs)
		return;

	let url = "";
	const value = encodeURI(msg.toLowerCase());
	if (typeOfFilter === TypeOfFilter.Regular || typeOfFilter === TypeOfFilter.Slider) {
		url = removeFromUrl ? updateParameter(filterQs.toLowerCase(), '') : updateParameter(filterQs.toLowerCase(), value);
	} else {
		const param = getParameter(filterQs.toLowerCase());
		let params = param ? param.split(",") : [];
		if (params.indexOf(value) < 0)
			params.push(value);
		else if (removeFromUrl)
			params = params.filter(f => f !== value);

		url = updateParameter(filterQs.toLowerCase(), params.join(","));
	}

	window.history.pushState({}, null, url.toLowerCase());
}
